/**
 * Beyond Niche motion layer - fallback path only.
 *
 * Browsers that support `animation-timeline: view()` need nothing from this
 * file; bn-motion.css drives those reveals entirely in CSS, off the main
 * thread. This script exists for the remainder (older Safari, older Android
 * WebViews) and does the least it can get away with.
 *
 * Ordering is load-bearing. `.bn-motion-js` is the ONLY thing that makes
 * `[data-bn-reveal]` transparent in the fallback path, and it is added after
 * every guard has passed and immediately before the observer starts watching.
 * A throw, a missing IntersectionObserver, or a reduced-motion preference all
 * leave the class off, which leaves the content visible. There is no ordering
 * of failures that can strand a reveal at opacity 0.
 */
/**
 * The bottle settle.
 *
 * Deliberately its own IIFE, ahead of the reveal fallback below. That one
 * returns early wherever `animation-timeline: view()` is supported, which is
 * most browsers now - putting the settle inside it would mean the settle only
 * ever ran on the browsers that need the fallback. It is the opposite: the
 * settle has no CSS-only path at all.
 *
 * The reveals can be stamped with `data-bn-reveal` at render time because they
 * live in bn-* Liquid blocks we own. The gallery does not - Horizon renders it
 * as its own <slideshow-component>, so there is no block of ours able to put
 * the attribute on it, and editing a core theme file to add one attribute is a
 * worse trade than applying it here.
 *
 * Waits for the image to decode. Scaling an <img> that has not painted settles
 * an empty box, and the motion is spent by the time the bottle is visible.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function settle() {
    const gallery = document.querySelector('media-gallery, slideshow-component');
    if (!gallery) return;

    // First slide only. The rest are off-screen, and settling a slide the
    // shopper has not arrived at yet spends the effect on nobody.
    const slide = gallery.querySelector('.product-media');
    const image = slide && slide.querySelector('img');
    if (!slide || !image || slide.hasAttribute('data-bn-settle')) return;

    const apply = () => slide.setAttribute('data-bn-settle', '');
    if (image.complete) apply();
    else image.addEventListener('load', apply, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', settle);
  } else {
    settle();
  }
})();

(function () {
  const REVEAL_SELECTOR = '[data-bn-reveal]';

  // The CSS already owns this case, so anything here would be duplicated work.
  if (window.CSS && CSS.supports && CSS.supports('animation-timeline: view()')) return;

  // No observer means no way to fire one-shot reveals; leave the page static.
  if (!('IntersectionObserver' in window)) return;

  // Matches the media query wrapping every rule in bn-motion.css.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.documentElement.classList.add('bn-motion-js');

  /**
   * `rootMargin`'s negative bottom inset holds the reveal until the element is
   * ~12% of the viewport in, so it resolves while being read rather than
   * flickering at the very edge of the screen during a fast scroll.
   */
  const observer = new IntersectionObserver(
    function (entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-bn-revealed');

        // One-shot. Replaying on every pass is the AOS behaviour NN/g found
        // actively frustrates task-oriented shoppers.
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0 }
  );

  function observe(root) {
    for (const element of root.querySelectorAll(REVEAL_SELECTOR)) {
      observer.observe(element);
    }
  }

  observe(document);

  /**
   * Section rendering (theme editor edits, cart drawer updates, the quick-add
   * modal) injects markup after this runs. Without re-observing, any reveal
   * inside that markup would keep the class-driven opacity: 0 forever - the one
   * way the fail-safe above could still be defeated. Cheap insurance: only
   * added nodes are walked, and each element is unobserved once it fires.
   */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(REVEAL_SELECTOR)) observer.observe(node);
          observe(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
